import { type ReactNode, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion, type Variants } from "framer-motion";
import { useLocation } from "react-router-dom";

/**
 * Shared motion primitives for the whole app.
 * Every helper respects the user's "prefers-reduced-motion" setting.
 */

// Signature easing used across the app for a polished, modern feel
export const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;

export const SPRING_SMOOTH = {
  type: "spring" as const,
  stiffness: 260,
  damping: 26,
};

export const SPRING_BOUNCY = {
  type: "spring" as const,
  stiffness: 400,
  damping: 17,
};

// ---------------------------------------------------------------------------
// Page transition (used around the router in App.tsx)
// ---------------------------------------------------------------------------

const pageVariants: Variants = {
  initial: { opacity: 0, y: 12, scale: 0.995 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.35, ease: EASE_OUT_EXPO },
  },
  exit: {
    opacity: 0,
    y: -8,
    scale: 0.995,
    transition: { duration: 0.18, ease: "easeIn" },
  },
};

/**
 * Wraps routed content so every navigation cross-fades + slides beautifully.
 * Keyed by pathname; AnimatePresence handles enter/exit.
 */
export const PageTransition = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  // Always land at the top of a new page (skips hash-anchor navigation)
  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  if (reduceMotion) return <>{children}</>;

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="min-h-screen"
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

// ---------------------------------------------------------------------------
// FadeIn — single element entrance (fade + directional slide)
// ---------------------------------------------------------------------------

type Direction = "up" | "down" | "left" | "right" | "none";

const offsetFor = (direction: Direction, distance: number) => {
  switch (direction) {
    case "up": return { y: distance };
    case "down": return { y: -distance };
    case "left": return { x: distance };
    case "right": return { x: -distance };
    default: return {};
  }
};

interface FadeInProps {
  children: ReactNode;
  /** Slide-in direction (content moves *from* this offset) */
  direction?: Direction;
  delay?: number;
  duration?: number;
  distance?: number;
  /** Animate when scrolled into view instead of on mount */
  inView?: boolean;
  className?: string;
}

export const FadeIn = ({
  children,
  direction = "up",
  delay = 0,
  duration = 0.5,
  distance = 24,
  inView = false,
  className,
}: FadeInProps) => {
  const reduceMotion = useReducedMotion();
  if (reduceMotion) return <div className={className}>{children}</div>;

  const hidden = { opacity: 0, ...offsetFor(direction, distance) };
  const visible = {
    opacity: 1,
    x: 0,
    y: 0,
    transition: { duration, delay, ease: EASE_OUT_EXPO },
  };

  return (
    <motion.div
      className={className}
      initial={hidden}
      {...(inView
        ? { whileInView: visible, viewport: { once: true, margin: "-40px" } }
        : { animate: visible })}
    >
      {children}
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// Stagger — parent/child pair for orchestrated list entrances
// ---------------------------------------------------------------------------

export const staggerContainerVariants: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.07, delayChildren: 0.05 },
  },
};

export const staggerItemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: EASE_OUT_EXPO },
  },
};

interface StaggerContainerProps {
  children: ReactNode;
  className?: string;
  /** Animate when scrolled into view instead of on mount */
  inView?: boolean;
}

export const StaggerContainer = ({ children, className, inView = false }: StaggerContainerProps) => {
  const reduceMotion = useReducedMotion();
  if (reduceMotion) return <div className={className}>{children}</div>;

  return (
    <motion.div
      className={className}
      variants={staggerContainerVariants}
      initial="hidden"
      {...(inView
        ? { whileInView: "visible", viewport: { once: true, margin: "-40px" } }
        : { animate: "visible" })}
    >
      {children}
    </motion.div>
  );
};

export const StaggerItem = ({ children, className }: { children: ReactNode; className?: string }) => {
  const reduceMotion = useReducedMotion();
  if (reduceMotion) return <div className={className}>{children}</div>;

  return (
    <motion.div className={className} variants={staggerItemVariants}>
      {children}
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// HoverLift — interactive wrapper (hover lift + tap press) for cards/CTAs
// ---------------------------------------------------------------------------

export const HoverLift = ({ children, className }: { children: ReactNode; className?: string }) => {
  const reduceMotion = useReducedMotion();
  if (reduceMotion) return <div className={className}>{children}</div>;

  return (
    <motion.div
      className={className}
      whileHover={{ y: -4, scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
      transition={SPRING_SMOOTH}
    >
      {children}
    </motion.div>
  );
};

export { motion, AnimatePresence, useReducedMotion };
